import { Icons } from '@/components/icon';
import { CountryFlag } from '@/components/ui/country-flag';
import { Badge, Button, IconButton, PageHeader } from '@/components/ui/design-system';
import { Modal } from '@/components/ui/design-system/modal/modal';
import { FEATURES } from '@/constants/features';
import { useTeamNumbersWithMembers } from '@/hooks/api/team';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import { getNumberAndCountryCode } from '@/lib/phone-number';
import { useAppChrome } from '@/providers/app-chrome/app-chrome-provider';
import { $apiAllo } from '@/services/api-allo/client';
import { IvrFlowEditor } from '@/views/number-settings/components/ivr-flow/ivr-flow-editor';
import { useFlowStore } from '@/views/number-settings/components/ivr-flow/lib/store';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

/**
 * Call-flow editor, inside the `_app` shell as a full-attention surface: the
 * top banners persist untouched, the sidebar and call bar slide away through
 * their AppChrome animations (Power Dialer focus-mode precedent), and the
 * editor renders as the same floating card as every other page. Living in the
 * shell is what makes the settings <-> editor transition feel continuous —
 * only the card content actually changes.
 */
export const Route = createFileRoute('/_app/call-flow/$number')({
  component: RouteComponent,
});

function RouteComponent() {
  const { number } = Route.useParams();
  const navigate = useNavigate();
  const { setHideSidebar, setHideCallBar } = useAppChrome();

  const goToNumberSettings = () =>
    navigate({ to: '/settings/numbers/$number', params: { number } });

  // Flag-gated: the editor only exists behind `ivr_canvas_view`. `isLoading`
  // distinguishes "flag off" from "flags not resolved yet" so a direct
  // navigation / refresh doesn't bounce before the answer is known.
  const isCanvasEnabled = useFeatureToggle(FEATURES.IVR_CANVAS_VIEW);
  const { isLoading: areFlagsLoading } = $apiAllo.useQuery('get', '/v1/feature-flags', {});

  const { data: teamNumbers } = useTeamNumbersWithMembers();
  const { number: nationalNumber, countryCode } = getNumberAndCountryCode(number);
  const lineName = teamNumbers?.find((entry) => entry.number === number)?.line_name;

  // Draft/publish model (mock store; the backend owns this once wired).
  const isDirty = useFlowStore((state) => state.isDirty);
  const publish = useFlowStore((state) => state.publish);
  const discard = useFlowStore((state) => state.discard);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);

  useEffect(() => {
    if (!areFlagsLoading && !isCanvasEnabled) {
      navigate({ to: '/settings/numbers/$number', params: { number }, replace: true });
    }
  }, [areFlagsLoading, isCanvasEnabled, navigate, number]);

  // Full-attention surface: chrome hides while the editor is open and slides
  // back on the way out (the AppChrome wrappers animate both directions). Only
  // when the canvas is actually enabled — otherwise we're redirecting away.
  useEffect(() => {
    if (!isCanvasEnabled) {
      return;
    }
    setHideSidebar(true);
    setHideCallBar(true);
    return () => {
      setHideSidebar(false);
      setHideCallBar(false);
    };
  }, [isCanvasEnabled, setHideSidebar, setHideCallBar]);

  if (!isCanvasEnabled) {
    return null;
  }

  return (
    <section className="relative flex h-full w-full flex-col">
      {/* Same fade-in as the app shell itself — simple and fluid. */}
      <div className="fade-in flex h-full w-full animate-in flex-col overflow-hidden rounded-2xl border border-primary-10 bg-white duration-150">
        <PageHeader
          hideSidebarToggle
          parent={
            <IconButton
              variant="ghost"
              size="sm"
              icon={Icons.ArrowLeft}
              label="Back to number settings"
              onClick={goToNumberSettings}
            />
          }
          title="Phone menu (IVR)"
          titleTrailing={
            <div className="flex items-center gap-1.5">
              <span className="h-4 w-px shrink-0 bg-primary-10" aria-hidden="true" />
              {countryCode && <CountryFlag countryCode={countryCode} size="m" />}
              {lineName && <span className="text-body-13 text-primary">{lineName}</span>}
              <span className="text-body-12 text-primary-50 tabular-nums">{nationalNumber}</span>
              {isDirty && (
                <Badge variant="gray" size="sm">
                  Unpublished
                </Badge>
              )}
            </div>
          }
          rightAddOn={
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!isDirty}
                onClick={() => setIsDiscardOpen(true)}
              >
                Discard
              </Button>
              <Button variant="primary" size="sm" disabled={!isDirty} onClick={publish}>
                Publish changes
              </Button>
            </div>
          }
        />
        <div className="min-h-0 flex-1">
          <IvrFlowEditor currentNumber={number} />
        </div>
      </div>

      <Modal
        variant="dialog"
        isOpen={isDiscardOpen}
        onClose={() => setIsDiscardOpen(false)}
        title="Discard unpublished changes?"
        description="This reverts the call flow to the last published version. Your unpublished edits will be lost."
        actions={[
          <Button key="cancel" variant="outline" size="sm" onClick={() => setIsDiscardOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="discard"
            variant="destructive"
            size="sm"
            onClick={() => {
              discard();
              setIsDiscardOpen(false);
            }}
          >
            Discard changes
          </Button>,
        ]}
      />
    </section>
  );
}
